// ---------------------------------------------------------------------------
// The REVA spec as the AVM API returns it: the raw response of the spec GET
// request (`reva.*`/`avm.*`), plus `SEGMENT` as the set of property segments
// the spec covers.
//
// Everything reachable from `RevaSpecApiResponse` belongs here. The converted,
// app-facing shapes and the conversion itself live in
// `@reva/shared/spec-conversion`.
// ---------------------------------------------------------------------------

/** Raw AVM Spec Endpoints */
export type AvmSpecEndpoint =
    | "BUILDING_CATEGORY"
    | "COMPARATIVE_PRICE"
    | "COMPARATIVE_VALUE"
    | "COMPARATIVE_VALUE_METRICS"
    | "ENERGY_VALUE"
    | "INCOME_VALUE"
    | "LOCATION_INFORMATION"
    | "LOCATION_INFORMATION_CLIMATE_RISK"
    | "LOCATION_INFORMATION_DESCRIPTION"
    | "LOCATION_INFORMATION_EMISSION"
    | "LOCATION_INFORMATION_MICRO_MACRO"
    | "LOCATION_INFORMATION_MICRO_MACRO_TEXT"
    | "LOCATION_INFORMATION_MUNICIPALITY_TYPE"
    | "LOCATION_INFORMATION_NEIGHBORHOOD"
    | "LOCATION_INFORMATION_NOISE"
    | "LOCATION_INFORMATION_POIS"
    | "LOCATION_INFORMATION_PRICE_INDEX"
    | "LOCATION_INFORMATION_STANDARD_GROUND_VALUE"
    | "LOCATION_INFORMATION_TIMELINES"
    | "MODERNIZATION_DEGREE"
    | "QUALITY_FURNISHINGS"
    | "REAL_ASSET_VALUE"
    | "VALUE";

/** Raw AVM Spec Segments */
export const SEGMENT = {
    MFHWGH_K: "MFHWGH_K",
    GRST_K: "GRST_K",
    BUERO_K: "BUERO_K",
    WHG_K: "WHG_K",
    WHG_M: "WHG_M",
    BUERO_M: "BUERO_M",
    EZFH_K: "EZFH_K",
} as const;

export type AvmSpecSegment = (typeof SEGMENT)[keyof typeof SEGMENT];

/** Raw category of a converted output-ref (`reva.<endpoint>.rows[].<cell>.categories[]`). */
export interface RawRevaSpecOutputCategory {
    /** Numeric value in the parameter that corresponds to this category. */
    categoryNumber: number;
    /** Short name for the category (DE - German). */
    nameDe: string;
    /** Short name for the category (EN - English). */
    nameEn: string;
    /** Title (long name) for the category (DE - German). */
    descriptionDe: string;
    /** Title (long name) for the category (EN - English). */
    descriptionEn: string;
}

/** Raw category of an api-param definition (`avm.<spec>.{input,output}Parameters[].categories[]`). */
export interface AvmSpecParamCategory {
    /** Numeric value in the parameter that corresponds to this category. */
    categoryNumber: number;
    /** Short name for the category (DE - German). */
    shortNameDe: string;
    /** Short name for the category (EN - English). */
    shortNameEn: string;
    /** Title (long name) for the category (DE - German). */
    titleDe: string;
    /** Title (long name) for the category (EN - English). */
    titleEn: string;
    /** Group the category is shown in (DE - German); absent for ungrouped categories (#19431). */
    groupDe?: string;
    /** Group the category is shown in (EN - English); absent for ungrouped categories (#19431). */
    groupEn?: string;
}

/** Raw output-ref cell of an output page row. */
export interface RawRevaSpecOutputRef {
    /** API key of the parameter to show here */
    apiParam: string;
    /** Flag whether the references apiParam should be associated to an input parameter (value true) or an output parameter (value false). */
    input: boolean;
    /** Display Format for the cell */
    displayFormat: string;
    /** Description of the parameter (DE - German). */
    descriptionDe?: string;
    /** Description of the parameter (EN - English). */
    descriptionEn?: string;
    /** Optional unit for the parameter value (DE - German). */
    unitDe?: string;
    /** Optional unit for the parameter value (EN - English). */
    unitEn?: string;
    /** Optional Categories of the parameter (only for data type "category" parameters). */
    categories?: RawRevaSpecOutputCategory[];
    /** Flag whether the cell should be hidden (or kept without content) if the referenced parameter has no value. */
    hideIfEmpty: boolean;
    /** Optional fallback value to display, when the cell is not hidden and the referenced parameter has no value. */
    valueIfEmpty?: string | null;
    /** Associated input parameter (only for output parameters). */
    associatedInputApiParam?: string | null;
    /** Optional api-param key of a "low" companion value, shown as part of the cell's value range. */
    lowApiParam?: string | null;
    /** Optional api-param key of a "high" companion value, shown as part of the cell's value range. */
    highApiParam?: string | null;
}

/** Raw row group of an output page. */
export interface RawRevaSpecGroup {
    /** Key of the groups (to be associated in a row). */
    group: string;
    /** Displayed title of the group (DE - German). */
    titleDe: string;
    /** Displayed title of the group (EN - English). */
    titleEn: string;
    /** Flag whether the group should be displayed unfolded/expanded without any use input (false means the group is folded/collapsed). */
    unfoldInitially: boolean;
    /** Optional setting: if array is not empty, the group shall be hidden, unless one of the listed parameters have a non-empty value (either from input or output). */
    hideUnlessApiParametersExisting?: string[] | null;
}

export type RawRevaFormatOption = "BOLD" | "GRAYED_OUT" | "LINE_ABOVE" | "SPACE_ABOVE" | "BLANK_LINE_ABOVE";

/** Raw row of an output page. */
export interface RawRevaSpecRow {
    /** Group this row belongs to. */
    group: string;
    /** Displayed title of the row (DE - German). */
    titleDe: string;
    /** Displayed title of the row (EN - English). */
    titleEn: string;
    /** Optional: format flag for the rendering of the row */
    formats?: RawRevaFormatOption[];
    /** Cell: "factor" (can be missing, if cell should not contain any value). */
    factor?: RawRevaSpecOutputRef;
    /** Cell: "market value factor" (can be missing, if cell should not contain any value). */
    marketValueFactor?: RawRevaSpecOutputRef;
    /** Cell: "market value" (can be missing, if cell should not contain any value). */
    marketValue?: RawRevaSpecOutputRef;
    /** Cell: "lending value factor" (can be missing, if cell should not contain any value). */
    lendingValueFactor?: RawRevaSpecOutputRef;
    /** Cell: "lending value" (can be missing, if cell should not contain any value). */
    lendingValue?: RawRevaSpecOutputRef;
    /** Optional setting: if array is not empty, the row shall be hidden, unless one of the listed parameters have a non-empty value (either from input or output). */
    hideUnlessApiParametersExisting?: string[] | null;
}

/** Raw output page spec (`reva.<endpoint>`). */
export interface RawRevaSpecPage {
    /** List of groups in the displayed grid */
    groups: RawRevaSpecGroup[];
    /** List of rows in the displayed grid (across all groups). */
    rows: RawRevaSpecRow[];
}

/** Classification of where a parameter originates (REVA-internal). */
export type RevaValueParameterType =
    | "AVM"
    | "META"
    | "DOC_PROPERTY"
    | "REGISTER"
    | "ENERGY_CERT"
    | "IDP_ENERGY_CERTIFICATE"
    | "PLOT"
    | "INSPECTION_DERIVED"
    | "FIRST_FINALIZE"
    | "IMMODROP";

/** Possible data types for Reva Input Rows */
export type RevaSpecInputRowType = "category" | "date" | "double" | "integer" | "string" | "text" | "toggle";

export interface RevaSpecObjectInputParameterDetails {
    /** Parameter key this details belong to (should be the same as the parameter row, this details object is embedded into). */
    parameter: string;
    /** the data type of the parameter */
    type: RevaSpecInputRowType;
    /** Name of the parameter (DE - German).*/
    nameDe: string;
    /** Name of the parameter (EN - English).*/
    nameEn: string;
}

/** A disjoint value range of a "reva.input" row, rendered as select-buttons instead of a number input (#18858). */
export interface RevaSpecObjectInputRange {
    /** Value written to the parameter when this range's button is picked. */
    representativeValue: number;
    /** Lower bound (inclusive) of the range. */
    lowerValue: number;
    /** Upper bound (inclusive) of the range. */
    upperValue: number;
    /** Label of the range (DE - German). */
    nameDe: string;
    /** Label of the range (EN - English). */
    nameEn: string;
}

export interface RevaSpecObjectInputRow {
    /** Parameter key to use in this row. */
    parameter: string;
    /** Type/Source of parameter (defines, in which persistence layer the parameter for this row is stored). */
    parameterType: RevaValueParameterType;
    /** Details for the parameter (e.g. for parameter type "META" or "DOC_PROPERTY", and parameters which don't correspond to an existing AVM parameter). */
    details?: RevaSpecObjectInputParameterDetails | null;
    /** Optional: disjoint value ranges to render as select-buttons instead of the default number input (#18858). */
    ranges?: RevaSpecObjectInputRange[] | null;
    /** Override for the displayed parameter name (DE - German). */
    nameOverrideDe?: string | null;
    /** Override for the displayed parameter name (EN - English). */
    nameOverrideEn?: string | null;
    /** Formatting setting: render line above the input row. */
    lineAbove?: boolean | null;
    /** Key of (Meta-) Parameter this parameter input depends on. If there is a parameter dependence, and it has no value (false for boolean/toogle parameters), the row is not shown when empty. */
    dependsOn?: string | null;
    /** Optional: List of segments in Reva, for which the row should be shown. If empty, the row is shown in all segment. */
    onlyVisibleInSegments?: AvmSpecSegment[] | null;
}

export interface RevaSpecObjectInputBlock {
    /** key iof the input block, e.g. "cellar". */
    key: string;
    /** Optional title/header of the input block (DE - German). Block header is only rendered, if not empty. */
    titleDe?: string | null;
    /** Optional title/header of the input block (EN - English). Block header is only rendered, if not empty. */
    titleEn?: string | null;
    /** Optional: List of segments in Reva, for which the block should be shown. If empty, the block is shown in all segment. */
    onlyVisibleInSegments?: AvmSpecSegment[] | null;
    /** List of input rows in the block. */
    rows: RevaSpecObjectInputRow[];
}

export interface RevaSpecObjectInputColumn {
    /** key iof the input column, e.g. "right". */
    key: string;
    /** Title/headline of the column (DE - German).*/
    titleDe: string;
    /** Title/headline of the column (EN - English).*/
    titleEn: string;
    /** List of input blocks.*/
    blocks: RevaSpecObjectInputBlock[];
}

export interface RevaSpecObjectInputPage {
    /** Key of the page (e.g. "object/overview"). */
    key: string;
    /** List of columns for the input page.  */
    columns: RevaSpecObjectInputColumn[];
}

/** Raw input parameter group of an AVM spec. */
export interface AvmSpecInputParameterGroup {
    /** Group key (to be referenced in AVM input parameters). */
    key: string;
    /** Name of the group (DE - German). */
    nameDe: string;
    /** Name of the group (EN - English). */
    nameEn: string;
    /** Description of the group (DE - German). */
    descriptionDe: string;
    /** Description of the group (EN - English). */
    descriptionEn: string;
}

/** Rounding modes in format options. */
export type AvmSpecFormattingRoundingMode = "Down" | "Up" | "Round";

export interface AvmSpecFormattingOptions {
    /** Flag: should a thousands-separator be rendered (e.g. disabled for year numbers) */
    thousandsSeparator: boolean;
    /** Minimum number of rendered decimal places */
    minDecimalPlaces: number;
    /** Maximum number of rendered decimal places */
    maxDecimalPlaces: number;
    /** Rounding mode to use when a number with a higher precision is rounded according to the maxDecimalPlaces. */
    roundingMode: AvmSpecFormattingRoundingMode;
}

/** AVM base data types (without collections) */
export type AvmSpecParamDataTypeBase =
    | "integer"
    | "long"
    | "double"
    | "boolean"
    | "category"
    | "string"
    | "json"
    | "date"
    | "timestamp";

/** AVM data types (either a base type, or a collection type by appending "[]") */
export type AvmSpecParamDataType = AvmSpecParamDataTypeBase | `${AvmSpecParamDataTypeBase}[]`;

/**
 * A minimum or maximum value a parameter can take, in the shape the AVM spec delivers it.
 *
 * The shape follows the parameter's data type: numeric types carry a number, `date` carries an ISO
 * date string (e.g. `"2018-01-01"`). Bounds only exist for numeric and date data types.
 *
 * Note for comparing a value against a bound: ISO 8601 date strings of equal length order
 * lexicographically, so a plain string comparison is correct for `date` parameters.
 */
export type AvmSpecParameterBound = number | string;

/** Raw input parameter of an AVM spec. */
export interface AvmSpecInputParameter {
    /** API key for the parameter - under this name the parameter is referenced everywhere, and this is the key for the parameter in the indication request JSON. */
    parameter: string;
    /** Reference to the input parameter group the input parameter belongs to. */
    group: string;
    /** Input parameter data type. */
    type: AvmSpecParamDataType;
    /** Optional example value for the input parameter. */
    exampleValue?: unknown;
    /** Name of the parameter (DE - German). */
    nameDe: string;
    /** Name of the parameter (EN - English). */
    nameEn: string;
    /** Description of the parameter (DE - German). */
    descriptionDe?: string;
    /** Description of the parameter (EN - English). */
    descriptionEn?: string;
    /** Additional GUI help text for the parameter (DE - German); may contain HTML, e.g. tables (#19411). */
    guiAdditionalHelpTextDe?: string | null;
    /** Additional GUI help text for the parameter (EN - English); may contain HTML, e.g. tables (#19411). */
    guiAdditionalHelpTextEn?: string | null;
    /** Optional unit for the parameter value (DE - German). */
    unitDe?: string | null;
    /** Optional unit for the parameter value (EN - English). */
    unitEn?: string | null;
    /** Optional additional hints for the parameter. */
    additionalHints?: string | null;
    /** Categories for the parameter (only if `type = 'category'`). */
    categories?: AvmSpecParamCategory[];
    /** Flag whether the parameter is required for the indication and the selected segment. */
    required: boolean;
    /** Optional minimum value this parameter can have for the endpoint and segment. */
    minValue?: AvmSpecParameterBound | null;
    /** Optional maximum value this parameter can have for the endpoint and segment. */
    maxValue?: AvmSpecParameterBound | null;
    /** Optional minimum number of array elements (only for collection parameters). */
    arrayMinSize?: number | null;
    /** Optional maximum number of array elements (only for collection parameters). */
    arrayMaxSize?: number | null;
    /** Optional Flag if array must only contain non-null elements (only for collection parameters). */
    arrayElementsNotNull?: boolean | null;
    /** Display format to precisely define how the parameter is rendered (e.g. number of decimal points for numeric data types). */
    displayFormat: string;
    /** Formatting options (only form numeric data types). */
    formatting?: AvmSpecFormattingOptions;
}

/** Raw JSON model, reference in output parameters of an AVM spec. */
export interface AvmSpecOutputModel {
    /** name of the model. */
    modelName: string;
    /** Description of the model (DE - German). */
    descriptionDe?: string;
    /** Description of the model (EN - English). */
    descriptionEn?: string;
    /** Properties of the JSON model. */
    properties: AvmSpecOutputModelParameter[];
}

/** Raw output parameter of an AVM spec. */
export interface AvmSpecOutputParameter {
    /** API key for the parameter - under this name the parameter is referenced everywhere, and this is the key for the parameter in the indication response JSON. */
    parameter: string;
    /** Output parameter data type. */
    type: AvmSpecParamDataType;
    /** Optional example value for the output parameter. */
    exampleValue?: unknown;
    /** Name of the parameter (DE - German). */
    nameDe: string;
    /** Name of the parameter (EN - English). */
    nameEn: string;
    /** Description of the parameter (DE - German). */
    descriptionDe?: string;
    /** Description of the parameter (EN - English). */
    descriptionEn?: string;
    /** Optional unit for the parameter value (DE - German). */
    unitDe?: string | null;
    /** Optional unit for the parameter value (EN - English). */
    unitEn?: string | null;
    /** Optional additional hints for the parameter. */
    additionalHints?: string | null;
    /** Categories for the parameter (only if `type = 'category'`). */
    categories?: AvmSpecParamCategory[];
    /** Optional reference to an output model (only if the data type is "json"). */
    model?: AvmSpecOutputModel;
    /** Optional flag: is the value an array of the referenced model (or only a single instance). */
    modelAsArray?: boolean;
    /** Display format to precisely define how the parameter is rendered (e.g. number of decimal points for numeric data types). */
    displayFormat: string;
    /** Formatting options (only form numeric data types). */
    formatting?: AvmSpecFormattingOptions;
}

/** Raw JSON property of an AVM output model. */
export interface AvmSpecOutputModelParameter
    extends Omit<AvmSpecOutputParameter, "parameter" | "additionalHints" | "displayFormat" | "formatting"> {
    /** JSON Property name for this JSON model parameter. */
    property: string;
}

/** Raw AVM spec (`avm.<spec>`). */
export interface AvmSpec {
    /** Indication endpoint. */
    indication: AvmSpecEndpoint;
    /** Segments, for which the indication is supported. */
    segments: AvmSpecSegment[];
    /** Example request containing example values of all parameters. */
    exampleRequest: Record<string, unknown>;
    /** List of parameter groups for the AVM input parameters (sorted). */
    inputParameterGroups: AvmSpecInputParameterGroup[];
    /** Map of allowed input parameters by segment (key is segment, value is list of input parameters). */
    inputParameters: Record<AvmSpecSegment, AvmSpecInputParameter[]>;
    /** List of all output for this indication endpoint. */
    outputParameters: AvmSpecOutputParameter[];
}

/** Raw AVM spec (`avm.<spec>`) for avm endpoints which are "output-only" and have no input configuration. */
export type AvmSpecOutputOnly = Omit<
    AvmSpec,
    "segments" | "exampleRequest" | "inputParameterGroups" | "inputParameters"
>;

/** The complete raw spec response. */
export interface RevaSpecApiResponse {
    /** Reva Spec: outputs for page "value" (or sub-tabs on page "object"), inputs for page "object". */
    reva: {
        /** Output page - tab "value indication" */
        VALUE: RawRevaSpecPage;
        /** Output page - tab "comparative value indication" */
        COMPARATIVE_VALUE: RawRevaSpecPage;
        /** Output page - tab "income value indication" */
        INCOME_VALUE: RawRevaSpecPage;
        /** Output page - tab "real asset value indication" */
        REAL_ASSET_VALUE: RawRevaSpecPage;
        /** Output page - tab "energy certificate" on page "object" */
        ENERGY_VALUE: RawRevaSpecPage;
        /** Inputs */
        input: RevaSpecObjectInputPage[];
    };
    /** Embedded AVM Specs, for each AVM Endpoint used in REVA. */
    avm: {
        /** Spec for: Value Indication. */
        VALUE: AvmSpec;
        /** Spec for: Energy Value Indication. */
        ENERGY_VALUE: AvmSpec;
        /** Spec for: Comparative Value Indication. */
        COMPARATIVE_VALUE: AvmSpec;
        /** Spec for: Object Information - Building category. */
        BUILDING_CATEGORY: AvmSpec;
        /** Spec for: Object Information - Modernization degree. */
        MODERNIZATION_DEGREE: AvmSpec;
        /** Spec for: Object Information - Quality furnishings. */
        QUALITY_FURNISHINGS: AvmSpec;
        /** Spec for: Location Information - Price Index. */
        LOCATION_INFORMATION_PRICE_INDEX: AvmSpecOutputOnly;
        /** Spec for: Location Information - Standard Ground Value (german: "Bodenrichtwert", "BRW"). */
        LOCATION_INFORMATION_STANDARD_GROUND_VALUE: AvmSpecOutputOnly;
        /** Spec for: Location Information - Climate Risk (german: "Klimarisiken"). */
        LOCATION_INFORMATION_CLIMATE_RISK: AvmSpecOutputOnly;
        /** Spec for: Location Information - Description (macro/micro/market location texts). */
        LOCATION_INFORMATION_DESCRIPTION: AvmSpecOutputOnly;
    };
}
